import posthog from 'posthog-js'

/**
 * Marketing Attribution URL Parameters
 * 
 * These are the parameters passed from the marketing site to maintain
 * accurate attribution when users transition from marketing → app.
 */
export const MARKETING_URL_PARAMS = {
  /** The anonymous distinct_id from the marketing site visitor */
  DISTINCT_ID: 'ph_distinct_id',
  /** The session_id from the marketing site (for session continuity) */
  SESSION_ID: 'ph_session_id',
} as const

/**
 * Handles marketing attribution by linking the marketing site's anonymous
 * visitor to the app user and preserving attribution data.
 * 
 * This should be called early in the app lifecycle, ideally before
 * or immediately after PostHog.identify() is called.
 * 
 * How it works:
 * 1. Checks for ph_distinct_id from the marketing site URL
 * 2. Uses posthog.alias() to merge the marketing visitor with the current user
 * 3. PostHog automatically captures UTM params, so we don't need to manually set them
 * 
 * @returns boolean - true if attribution was processed, false otherwise
 */
export function handleMarketingAttribution(): boolean {
  const params = new URLSearchParams(window.location.search)
  const marketingDistinctId = params.get(MARKETING_URL_PARAMS.DISTINCT_ID)

  if (!marketingDistinctId) {
    return false
  }

  // Validate the distinct_id format (should be a non-empty string)
  if (typeof marketingDistinctId !== 'string' || marketingDistinctId.trim() === '') {
    console.warn('[PostHog Attribution] Invalid marketing distinct_id received')
    return false
  }

  try {
    // Use alias to merge the marketing site anonymous user with the current app user
    // This creates a link between the two distinct_ids in PostHog
    // All past events from the marketing site will now be associated with this user
    posthog.alias(marketingDistinctId)
    
    if (import.meta.env.MODE === 'development') {
      console.log('[PostHog Attribution] Successfully linked marketing visitor:', marketingDistinctId)
    }

    return true
  } catch (error) {
    console.error('[PostHog Attribution] Failed to alias marketing visitor:', error)
    return false
  }
}

/**
 * Cleans the marketing attribution parameters from the URL
 * after they've been processed.
 * 
 * This provides a cleaner URL for the user and prevents
 * re-processing the same parameters on subsequent navigations.
 */
export function cleanMarketingParamsFromUrl(): void {
  const url = new URL(window.location.href)
  let hasParams = false

  Object.values(MARKETING_URL_PARAMS).forEach((param) => {
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param)
      hasParams = true
    }
  })

  if (hasParams) {
    // Replace the URL without the marketing params (doesn't trigger navigation)
    window.history.replaceState({}, '', url.toString())
  }
}

/**
 * Checks if the current URL contains marketing attribution parameters
 */
export function hasMarketingAttribution(): boolean {
  const params = new URLSearchParams(window.location.search)
  return params.has(MARKETING_URL_PARAMS.DISTINCT_ID)
}

